import { styled, type Style } from 'inlines'
import type { ReactNode } from 'react'
import { match } from 'ts-pattern'
import { typographyStyles, TypographyVariant } from './styles'

export type TextProps = {
  children: ReactNode
  isBold?: boolean
  color?: string
  style?: Style
}

const BaseText = ({
  children,
  style,
  isBold,
  color = 'var(--brand-secondary)',
  variant,
}: TextProps & { variant: TypographyVariant }) => {
  const config = typographyStyles[variant]
  const fontWeight = isBold
    ? config.isBoldFontWeight || 500
    : config.fontWeight || 400

  return (
    <styled.span
      style={{
        color,
        fontSize: config.fontSize,
        fontWeight,
        lineHeight: config.lineHeight,
        ...style,
      }}
    >
      {children}
    </styled.span>
  )
}

const Large = (props: TextProps) => <BaseText {...props} variant="large" />
const Medium = (props: TextProps) => <BaseText {...props} variant="medium" />
const Small = (props: TextProps) => <BaseText {...props} variant="small" />
const ExtraSmall = (props: TextProps) => (
  <BaseText {...props} variant="extraSmall" />
)
const Subtitle = (props: TextProps) => (
  <BaseText {...props} variant="subtitle" />
)

const FormError = ({
  children,
  style,
}: Pick<TextProps, 'children' | 'style'>) => (
  <BaseText variant="extraSmall" color="var(--status-error)" style={style}>
    {children}
  </BaseText>
)

const TextPropsBased = (
  props: TextProps & {
    text: 'large' | 'medium' | 'small' | 'extraSmall'
  },
) => {
  return match(props.text)
    .with('large', () => Large(props))
    .with('medium', () => Medium(props))
    .with('small', () => Small(props))
    .with('extraSmall', () => ExtraSmall(props))
    .exhaustive()
}

const TextRoot = ({ children }: { children: ReactNode }) => {
  return <>{children}</>
}

TextRoot.Large = Large
TextRoot.Medium = Medium
TextRoot.Small = Small
TextRoot.ExtraSmall = ExtraSmall
TextRoot.Subtitle = Subtitle
TextRoot.FormError = FormError
TextRoot.Sized = TextPropsBased

export const Text = TextRoot
